import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PortfolioAs } from '../PortfolioSubnav.component';
import LinkToIcon from 'icons/LinkTo.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { getDataRoomRoute } from 'utils';
import { DATA_ROOM_LOGIN_SOURCE } from 'models/DataRoomLoginSource';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

type UsePortfolioLinksType = {
  as: PortfolioAs;
  dataRoomId: string;
  hasListing: boolean;
  sessionId?: string;
  unreadMessagesCount?: number;
  canAccessDataRoom: boolean;
  canSeeAllTabs?: boolean;
};

export default function usePortfolioLinks(config: UsePortfolioLinksType) {
  const {
    as,
    dataRoomId,
    hasListing,
    sessionId,
    unreadMessagesCount,
    canAccessDataRoom,
    canSeeAllTabs,
  } = config;

  const { isRWCMember, currentUser } = useContext(CurrentUserContext);

  const { seenAutomationFlow } = currentUser || {};

  const { t } = useTranslation();

  const isPhablet = useMatchMedia('isPhablet');

  const sellerLinks = useMemo(
    () => [
      {
        to: 'deal',
        label: t(isPhablet ? 'overview' : 'deal'),
      },
      ...(canSeeAllTabs
        ? [
            {
              to: 'leads',
              label: t('leads'),
            },
          ]
        : []),
      ...(!isRWCMember
        ? [
            {
              to: 'listing-information',
              label: t('listing'),
            },
          ]
        : []),
      ...(canSeeAllTabs
        ? [
            {
              to: 'settings',
              label: t('automation'),
              ...(!seenAutomationFlow && { className: 'new-feature' }),
            },
          ]
        : []),
      ...(canAccessDataRoom
        ? [
            {
              to: getDataRoomRoute(
                dataRoomId,
                undefined,
                DATA_ROOM_LOGIN_SOURCE.PORTFOLIO,
              ),
              label: t('dataRoomUC'),
              Icon: LinkToIcon,
              openInNewTab: true,
            },
          ]
        : []),
    ],
    [
      t,
      isPhablet,
      canSeeAllTabs,
      isRWCMember,
      seenAutomationFlow,
      canAccessDataRoom,
      dataRoomId,
    ],
  );

  const buyerLinks = useMemo(
    () => [
      ...(hasListing
        ? [
            {
              to: 'listing-information',
              label: t('deal'),
            },
          ]
        : []),
      ...(sessionId
        ? [
            {
              to: 'messaging',
              label: t('chat'),
              ...(!!unreadMessagesCount && { count: unreadMessagesCount }),
            },
          ]
        : []),
      {
        to: 'profile',
        label: t('buyerProfile'),
      },
      ...(canAccessDataRoom
        ? [
            {
              to: getDataRoomRoute(
                dataRoomId,
                'documents',
                DATA_ROOM_LOGIN_SOURCE.PORTFOLIO,
              ),
              label: t('dataRoomUC'),
              Icon: LinkToIcon,
              openInNewTab: true,
            },
          ]
        : []),
    ],
    [
      dataRoomId,
      canAccessDataRoom,
      hasListing,
      sessionId,
      unreadMessagesCount,
      t,
    ],
  );

  return { seller: sellerLinks, buyer: buyerLinks }[as];
}
